// Correspondance « par mots », insensible à la casse, aux accents et à
// l'ordre. Pendant JavaScript de `app/services/name_search.rb` : les deux
// implémentations partagent la même table d'accents et la même règle, pour
// qu'un filtre client et un filtre serveur donnent le même résultat.
//
// Le besoin : une ligne affichée « BIRONNEAU Olivia » doit ressortir aussi
// bien sur « Olivia Bironneau », « bironneau oli » que « olivia bir ». Un
// simple `haystack.includes(term)` échoue dès que l'utilisateur inverse
// prénom et nom, puisqu'il cherche une sous-chaîne contiguë.
//
//   import { matchesSearch } from "../utils/text_match"
//   matchesSearch(row.dataset.search, input.value)  // => true / false

// Même table que NameSearch::ACCENTS_FROM / ACCENTS_TO côté Ruby.
const ACCENTS_FROM = "àáâãäåçèéêëìíîïñòóôõöùúûüýÿšžćčřğşţűő";
const ACCENTS_TO = "aaaaaaceeeeiiiinooooouuuuyyszccrgstuo";

const ACCENT_MAP = {};
for (let i = 0; i < ACCENTS_FROM.length; i += 1) {
  ACCENT_MAP[ACCENTS_FROM[i]] = ACCENTS_TO[i];
}
const ACCENT_RE = new RegExp(`[${ACCENTS_FROM}]`, "g");

// Séparateurs de mots : identiques à NameSearch::SEPARATORS.
const SEPARATORS = /[\s\-_.,;:'’"()[\]\/\\@+]+/;

// Minuscule + repli des accents. La composition NFC d'abord, pour qu'un
// « e + accent combinant » devienne « é » avant le repli.
export function normalizeText(value) {
  return String(value ?? "")
    .normalize("NFC")
    .toLowerCase()
    .replace(ACCENT_RE, (c) => ACCENT_MAP[c] || c);
}

// Découpe la saisie en mots normalisés, sans doublon.
export function searchTokens(query) {
  const tokens = normalizeText(query).split(SEPARATORS).filter(Boolean);
  return [...new Set(tokens)];
}

// Tous les mots de la saisie doivent apparaître dans la chaîne cherchée,
// quel que soit leur ordre. Une saisie vide laisse tout passer.
export function matchesSearch(haystack, query) {
  const tokens = searchTokens(query);
  if (tokens.length === 0) return true;

  const hay = normalizeText(haystack);
  return tokens.every((token) => hay.includes(token));
}

// Variante pour les boucles : on normalise la saisie une seule fois, puis on
// teste chaque ligne. `makeMatcher("")` renvoie un prédicat toujours vrai.
export function makeMatcher(query) {
  const tokens = searchTokens(query);
  if (tokens.length === 0) return () => true;
  return (haystack) => {
    const hay = normalizeText(haystack);
    return tokens.every((token) => hay.includes(token));
  };
}

// Concatène des fragments en une chaîne normalisée (pendant de
// NameSearch.blob), utile quand le haystack est assemblé côté client.
export function searchBlob(...parts) {
  return normalizeText(parts.flat().filter(Boolean).join(" "))
    .replace(/\s+/g, " ")
    .trim();
}

// Matcher prêt à l'emploi pour Select2 : même signature que l'option
// `matcher` de Select2 4.x, à passer tel quel à `$el.select2({ matcher })`.
export function select2Matcher(params, data) {
  const term = (params && params.term) || "";
  if (!String(term).trim()) return data;
  if (typeof data.text === "undefined") return null;

  // Un optgroup : on descend dans ses enfants. Si aucun enfant ne correspond
  // mais que le libellé du groupe lui-même correspond, on remonte le groupe
  // entier (le picker de contacts groupé par entreprise en dépend).
  if (data.children) {
    const kept = data.children.filter((child) => select2Matcher(params, child) !== null);
    if (kept.length > 0) return { ...data, children: kept };
    return matchesSearch(data.text, term) ? data : null;
  }

  return matchesSearch(data.text, term) ? data : null;
}

export default matchesSearch;
